import { useEffect, useState } from 'react';
import { BookOpen, AlertTriangle } from 'lucide-react';

import { chequeBookApi } from '@/api/chequeBook.api';
import { chequeApi } from '@/api/cheque.api';
import type { ChequeRecordListDto } from '@/types/cheque.types';
import type { ChequeAllocation, ChequeBookOption } from '@/types/chequeBook.types';

/**
 * The cheque number on a payment line, and where it comes from (GAP-001).
 *
 * Three shapes, decided by the account and the company — not by this component:
 *
 *   Unmanaged account   free text, no hint. Nobody has registered a book, so there is nothing to
 *                       take a number from and nothing to check it against.
 *   MANUAL + managed    free text, with the book's own format and range shown. The server still
 *                       refuses a number that is used, voided, held, mis-shaped or out of range.
 *   AUTO + managed      pick a book; the next available leaf fills in and the field locks.
 *   Endorsing           pick a cheque already in hand; its number fills in and the field locks.
 *                       Not one of ours at all — see ML-12.
 *
 * The number is not "reserved" by choosing it here — a draft holds a leaf only once it is saved, so
 * two people composing vouchers at the same time can both see the same next number and the second
 * save is refused. Showing the count is what makes that recoverable: the operator can see the book
 * has more in it and pick again.
 */

export interface ChequeNumberValue {
  chequeNumber: string;
  /** Which book the number came from, in automatic mode. Empty in manual mode. */
  chequeBookId: string;
  /**
   * The received cheque being handed to the supplier, when this line endorses one rather than
   * writing a new cheque (ML-12). Empty on every ordinary line.
   */
  endorsedChequeId?: string;
}

/**
 * Which registered book a typed cheque number falls in, or null when none does.
 *
 * Compares the trailing digits, and only when the non-numeric part matches — a book numbered
 * "A 000100" and one numbered "B 000100" are different cheques. Deliberately gives up rather than
 * guessing when it cannot parse either end: this drives a warning, and a warning that fires on a
 * number the operator is holding in their hand is worse than no warning at all.
 */
const digitsOf = (value: string): bigint | null => {
  const match = value.trim().match(/(\d+)\s*$/);
  return match ? BigInt(match[1]) : null;
};

const prefixOf = (value: string): string =>
  value.trim().toUpperCase().replace(/\d+\s*$/, '').trim();

export const bookForNumber = (
  books: ChequeBookOption[], typed: string,
): ChequeBookOption | null => {
  const number = digitsOf(typed);
  if (number === null) return null;

  return books.find(b => {
    const first = digitsOf(b.firstChequeNumber);
    const last  = digitsOf(b.lastChequeNumber);
    if (first === null || last === null) return false;
    if (prefixOf(b.firstChequeNumber) !== prefixOf(typed)) return false;
    return number >= first && number <= last;
  }) ?? null;
};

interface Props {
  /** Null until the line names an account — until then there is nothing to ask about. */
  bankAccountId: string | null;
  /**
   * True when the line draws on Cheques-in-Hand rather than a bank: it is handing over a cheque a
   * customer gave us (ML-12). The number then comes from that cheque, not from our own stock.
   */
  endorsing?: boolean;
  value: ChequeNumberValue;
  onChange: (value: ChequeNumberValue) => void;
  disabled?: boolean;
}

export function ChequeNumberField({
  bankAccountId, value, onChange, disabled, endorsing = false,
}: Props) {
  const [inHand, setInHand] = useState<ChequeRecordListDto[]>([]);
  const [allocation, setAllocation] = useState<ChequeAllocation | null>(null);
  const [loading,    setLoading]    = useState(false);
  const [error,      setError]      = useState<string | null>(null);

  useEffect(() => {
    if (!bankAccountId) { setAllocation(null); return; }

    // The account decides the whole answer, so a stale response for the previous account would
    // offer a book that belongs to a different one. Ignore anything arriving after a change.
    let current = true;
    setLoading(true);
    setError(null);

    chequeBookApi.getAllocation(bankAccountId)
      .then(res => { if (current) setAllocation(res.data.data ?? null); })
      .catch(() => {
        if (!current) return;
        setAllocation(null);
        // Falling back to free text rather than blocking: the server enforces the book either way,
        // so a failed lookup should cost the operator a hint, not the ability to raise a payment.
        setError('Could not check the cheque books for this account.');
      })
      .finally(() => { if (current) setLoading(false); });

    return () => { current = false; };
  }, [bankAccountId]);

  // The cheques available to hand over. Fetched only when the line says it is endorsing, so an
  // ordinary payment never pays for a request it has no use for.
  useEffect(() => {
    if (!endorsing) { setInHand([]); return; }

    let current = true;
    chequeApi.inHandForEndorsement()
      .then(res => { if (current) setInHand(res.data.data?.items ?? []); })
      .catch(() => { if (current) setInHand([]); });

    return () => { current = false; };
  }, [endorsing]);

  const auto  = allocation?.mode === 'AUTO' && allocation.isManaged;
  const books = allocation?.books ?? [];
  const book  = books.find(b => b.chequeBookId === value.chequeBookId) ?? null;

  // In automatic mode the number is the book's, so a book with nothing left must not leave a stale
  // number behind from the book chosen before it.
  useEffect(() => {
    if (!auto) return;

    // One book and none chosen: choose it. Making the operator pick from a list of one is a click
    // that carries no decision.
    if (!value.chequeBookId && books.length === 1 && books[0].nextChequeNumber) {
      onChange({ chequeBookId: books[0].chequeBookId, chequeNumber: books[0].nextChequeNumber });
      return;
    }

    if (book && book.nextChequeNumber && value.chequeNumber !== book.nextChequeNumber)
      onChange({ chequeBookId: book.chequeBookId, chequeNumber: book.nextChequeNumber });
  }, [auto, books, book]);   // eslint-disable-line react-hooks/exhaustive-deps

  // Leaving automatic mode, or moving to an account nobody manages, must not strand a book id on
  // the line — the server would have no book to match it against.
  useEffect(() => {
    if (!auto && value.chequeBookId)
      onChange({ ...value, chequeBookId: '' });
  }, [auto]);   // eslint-disable-line react-hooks/exhaustive-deps

  // The next number the operator can actually use, for the placeholder. First book with anything
  // left — with one book that is simply its next, and with several it is the first that is not
  // finished, which is the one they are most likely reaching for.
  const example = books.find(b => b.nextChequeNumber)?.nextChequeNumber ?? null;

  const matchedBook = bookForNumber(books, value.chequeNumber);

  const inputClass =
    'w-full rounded border border-gray-300 dark:border-gray-600 px-2 py-1 text-sm ' +
    'disabled:bg-gray-100 dark:disabled:bg-gray-800 disabled:text-gray-500';

  // ── Endorsing: the cheque is the control, the number is a readout (ML-12) ──
  if (endorsing) {
    const chosen = inHand.find(c => c.id === value.endorsedChequeId) ?? null;

    return (
      <div className="space-y-1">
        <select
          value={value.endorsedChequeId ?? ''}
          disabled={disabled}
          aria-label="Cheque being endorsed"
          onChange={e => {
            const cheque = inHand.find(c => c.id === e.target.value);
            onChange({
              chequeBookId:     '',
              endorsedChequeId: cheque?.id ?? '',
              // The number on the paper, which is the drawer's, not ours.
              chequeNumber:     cheque?.chequeNumber ?? '',
            });
          }}
          className={inputClass}
        >
          <option value="">
            {inHand.length === 0 ? 'No cheques in hand to endorse' : 'Choose the cheque to hand over…'}
          </option>
          {inHand.map(c => (
            <option key={c.id} value={c.id}>
              {c.chequeNumber} · {c.customerName ?? c.supplierName ?? '—'} · {c.currencyCode}{' '}
              {c.amount.toLocaleString(undefined, { minimumFractionDigits: 2 })}
            </option>
          ))}
        </select>

        {chosen && (
          <p className="text-xs text-slate-500 dark:text-gray-400">
            Drawn by {chosen.customerName ?? '—'}. Endorsing hands the cheque over without banking
            it, so it never reaches your account.
          </p>
        )}

        {inHand.length === 0 && (
          <p className="text-xs text-amber-700 dark:text-amber-400">
            Nothing is in hand. A cheque has to be received and still undeposited before it can be
            passed on.
          </p>
        )}
      </div>
    );
  }

  // ── Automatic: the book is the control, the number is a readout ──────────
  if (auto) {
    return (
      <div className="space-y-1">
        <div className="flex items-center gap-1">
          <BookOpen className="w-3.5 h-3.5 shrink-0 text-orange-500" />
          <select
            value={value.chequeBookId}
            aria-label="Cheque book"
            disabled={disabled || loading}
            onChange={e => {
              const chosen = books.find(b => b.chequeBookId === e.target.value);
              onChange({
                chequeBookId: chosen?.chequeBookId ?? '',
                chequeNumber: chosen?.nextChequeNumber ?? '',
              });
            }}
            className={inputClass}
          >
            <option value="">{books.length === 0 ? 'No active book' : 'Cheque book…'}</option>
            {books.map(b => (
              <option key={b.chequeBookId} value={b.chequeBookId} disabled={b.availableLeaves === 0}>
                {b.bookCode} — {b.availableLeaves === 0
                  ? 'none left'
                  : `next ${b.nextChequeNumber} · ${b.availableLeaves} left`}
              </option>
            ))}
          </select>
        </div>

        <input
          value={value.chequeNumber}
          readOnly
          disabled
          placeholder="—"
          aria-label="Cheque number, taken from the book"
          className={inputClass + ' font-mono'}
        />

        {book?.availableLeaves === 0 && (
          <p className="flex items-start gap-1 text-xs text-amber-700 dark:text-amber-400">
            <AlertTriangle className="w-3 h-3 mt-0.5 shrink-0" />
            {book.bookCode} is finished. Register the next book under Setup → Cheque Books.
          </p>
        )}
      </div>
    );
  }

  // ── Manual, or an account nobody manages ─────────────────────────────────
  return (
    <div className="space-y-1">
      {/* A real number the operator can compare against the cheque in their hand, not the book's
          mask. "NNNNNN" is developer notation: it reads as text somebody forgot to replace, and
          nothing on screen says an N stands for a digit. */}
      <input
        value={value.chequeNumber}
        disabled={disabled}
        onChange={e => onChange({ chequeBookId: '', chequeNumber: e.target.value })}
        placeholder={example ? `e.g. ${example}` : 'Cheque no…'}
        className={inputClass + ' font-mono'}
      />

      {/* Said here rather than by the server after the whole voucher is composed. Only once they
          have typed something, and only when the number could be read at all — a warning that
          fires on a cheque the operator is holding would train them to ignore every warning. */}
      {allocation?.isManaged && books.length > 0 && value.chequeNumber.trim() !== '' && (
        !matchedBook && digitsOf(value.chequeNumber) !== null && (
          // Terse on purpose: this is the one thing that still costs a line in the grid, so it
          // says what is wrong and leaves the ranges to the line's detail.
          <p className="text-xs text-amber-700 dark:text-amber-400" data-testid="cheque-book-warning">
            Not in any book for this account
          </p>
        )
      )}

      {error && <p className="text-xs text-amber-700 dark:text-amber-400">{error}</p>}
    </div>
  );
}
